import React, { useState, useEffect } from "react";
import { DataItem } from "../definitions/internationalTypes";
import AlphabeticalNavigation from "./AlphabeticalNavigation";
import { getAvailableLetters } from "../utilities/alphabeticalGrouping";

interface AlphabeticalCountryListProps {
  alphabeticalData: Record<string, Record<string, DataItem[]>>;
  onCountryClick: (country: string, items: DataItem[]) => void;
  listType: "police" | "organization";
  className?: string;
}

const AlphabeticalCountryList: React.FC<AlphabeticalCountryListProps> = ({
  alphabeticalData,
  onCountryClick,
  listType,
  className = "",
}) => {
  const [activeLetters, setActiveLetters] = useState<string[]>([]);
  const availableLetters = getAvailableLetters(alphabeticalData);

  // Intersection Observer to track which letters are in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleLetters: string[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const letter = entry.target.id.replace("letter-", "");
            visibleLetters.push(letter);
          }
        });
        setActiveLetters(visibleLetters);
      },
      {
        rootMargin: "-20% 0px -70% 0px",
        threshold: 0.1,
      },
    );

    // Observe all letter sections
    availableLetters.forEach((letter) => {
      const element = document.getElementById(`letter-${letter}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [availableLetters]);

  const handleLetterClick = () => {
    // Navigation is handled in AlphabeticalNavigation component
  };

  const getColorClasses = (variant: "primary" | "secondary" = "primary") => {
    if (variant === "primary") {
      return listType === "police"
        ? "text-primary hover:text-primary/80"
        : "text-success hover:text-success/80";
    }
    return listType === "police"
      ? "border-primary/20 bg-primary/5"
      : "border-success/20 bg-success/5";
  };

  const getButtonColor = () => {
    return listType === "police" ? "btn-primary" : "btn-success";
  };

  if (Object.keys(alphabeticalData).length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="alert alert-info max-w-md mx-auto">
          <svg
            className="w-12 h-12 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <div>
            <p className="text-lg font-semibold">Tidak ada data ditemukan</p>
            <p className="text-sm text-base-content/70 mt-2">
              Data {listType === "police" ? "kepolisian" : "organisasi"}{" "}
              internasional belum tersedia
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Alphabetical Navigation */}
      <AlphabeticalNavigation
        availableLetters={availableLetters}
        activeLetters={activeLetters}
        onLetterClick={handleLetterClick}
      />

      {/* Letter Sections */}
      <div className="mt-8">
        {Object.entries(alphabeticalData)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([letter, countries]) => (
            <section
              key={letter}
              id={`letter-${letter}`}
              className="mb-12 scroll-mt-20"
            >
              {/* Letter Header */}
              <div className="mb-6 pb-2 border-b-2 border-base-300">
                <h2 className="text-4xl font-bold text-base-content mb-2">
                  {letter}
                </h2>
                <p className="text-sm text-base-content/60">
                  {Object.keys(countries).length} negara/institusi •{" "}
                  {Object.values(countries).reduce(
                    (total, items) => total + items.length,
                    0,
                  )}{" "}
                  dokumen
                </p>
              </div>

              {/* Countries Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-2">
                {Object.entries(countries)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([country, items]) => (
                    <div
                      key={country}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-base-200 border border-transparent hover:border-base-300 transition-all duration-150"
                    >
                      <button
                        onClick={() => onCountryClick(country, items)}
                        className={`text-left flex-grow cursor-pointer font-medium ${getColorClasses()}`}
                      >
                        {country}
                      </button>
                      <span className="text-sm text-base-content/60 ml-2">
                        ({items.length})
                      </span>
                    </div>
                  ))}
              </div>
            </section>
          ))}
      </div>

      {/* Back to Top Button */}
      <div className="fixed bottom-8 right-8">
        <button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className={`btn ${getButtonColor()} btn-circle shadow-lg hover:shadow-xl`}
          title="Kembali ke atas"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AlphabeticalCountryList;
